<template>
  <div>
    <div class="row">
      <div class="col-lg-3 col-md-4">
        <div class="sticky-top">
          <b-navbar class="flex-column " v-b-scrollspy:scrollspy-nested="config">
            <b-navbar-brand>
              <a href="https://www.projectco3.eu" target="_blank">
                <img class="w-100" :src="require('../assets/images/CO3_colored_secondary_version.svg')"/>
              </a>
            </b-navbar-brand>
            <b-nav pills vertical>
              <b-nav-item href="#main">What is CO3OTM</b-nav-item>
              <b-nav-item href="https://dev.co3.ontomap.eu/" target="_blank">APIs Description</b-nav-item>
              <b-nav-item href="#installation">Installation Instructions</b-nav-item>
              <b-nav pills vertical>
                <b-nav-item class="ml-3 my-1" href="#git_repo">Project Source Code</b-nav-item>
                <b-nav-item class="ml-3 my-1" href="#mongo">User Activity Logging</b-nav-item>
                <b-nav-item class="ml-3 my-1" href="#runApp">Run the application</b-nav-item>
              </b-nav>
              <b-dropdown-divider></b-dropdown-divider>
              <b-nav-item href="#licenses">Licenses</b-nav-item>
              <b-nav-item href="#privacy">Privacy Information</b-nav-item>
            </b-nav>
          </b-navbar>
        </div>
      </div>
      <div class="col-lg-9 col-md-8">
        <div id="scrollspy-nested">
          <div class="border rounded  bg-light shadow mb-3 p-3 mt-2">
            <h4 id="main">What is OnToMap Logging Service And Data Hub</h4>
            <p>The OnToMap Logging service and Data Hub (CO3OTM) has the following roles in the CO3 platform (the API of
              the Logger can be found at <a href="https://dev.co3.ontomap.eu/">https://dev.co3.ontomap.eu/</a>):</p>
            <ol>
              <li><i>semantic data interoperability among platform applications</i></li>
              <li><i>cross-application logging of user actions</i></li>
              <li><i>data hub for the CO3 applications</i></li>
              <li><i>user achievements management (for gamification)</i></li>
            </ol>
            <p> In detail:
            </p>
            <ol>
              <li>
                <p>
                  <span
                      class="font-weight-bold font-italic">Semantic data interoperability among platform applications</span>
                  CO3OTM defines a unified information representation format that represents an interlingua
                  specification
                  of geographic information and of user actions. Specifically it provides both syntactic
                  interoperability among applications,
                  based on the use of JSON (<a href="https://www.json.org">www.json.org</a>) as a
                  standard for the exchange of information, and semantic data interoperability: </p>
                <ul>
                  <li>
                    <span class="font-italic">Unified, semantic representation of the types of data used by the applications of the platform.</span>
                    As applications can use diverse conceptual models (and different lexical formats) to represent
                    information, heterogeneous data descriptions have to be reconciled in order to support their
                    sharing.
                    CO3OTM enables applications to share information collected about geographical objects, initiatives,
                    activities, and so forth, so each platform application can successfully interpret the data
                    collected by the other applications, regardless of the terminology they adopt. CO3OTM addresses this
                    issue by exploiting a semantic representation of information provided by OnToMap Ontology,
                    that is used as an interlingua among CO3 applications.
                    The OnToMap Ontology is defined in the OWL language (<a href="www.w3.org/OWL/">www.w3.org/OWL/</a>)
                    and its latest version is available at <a href="https://dev.co3.ontomap.eu/ontology/">https://dev.co3.ontomap.eu/ontology/</a>.
                  </li>
                  <li><span class="font-italic">Unified representation of user action types</span>, i.e., of the types
                    of actions that are
                    carried out by interacting with the other CO3 applications and have to be logged in order to
                    maintain a unified history of the events occurring within an instance of the platform.
                    CO3OTM defines a customizable set of types of actions that can be tracked at the level of the whole
                    platform
                    (e.g., object creation, object modification, etc.) in order to compose a centralized log of user
                    actions to be shared among applications.
                  </li>
                </ul>
              </li>
              <li><p>
                <span class="font-weight-bold font-italic">Cross-application logging of user actions. </span>
                CO3OTM is a cross-application, centralized collector of the history of actions performed by CO3 users by
                interacting with the CO3 applications, and that the applications decide to disclose in the platform.
                The goal is that of achieving a unified perspective on shared user behavior, so that each application
                can interact with the user by possibly taking her/his previous actions into account.
                <br/>The logger offers APIs to:
                <ul>
                  <li>push events that describe user actions in order to share them within an instance the platform:
                    either individual events, or lists of events in batch loading, can be pushed in a single interaction
                    with the logger.
                  </li>
                  <li>retrieve shared data concerning user actions and/or geographic information items previously shared
                    through the logger within an instance of the platform.
                  </li>
                </ul>
                <p>
                  CO3OTM integrates data shared by heterogeneous applications; it collects data about both user actions
                  and geographic objects that the actions are about.
                  The goal of the logger is that of enabling each application within an instance of the CO3 platform to
                  log user actions it has tracked, or to retrieve, in the interlingua format,
                  user actions tracked by other applications of the platform. CO3OTM offers various types of action
                  retrieval filters, e.g.,
                  to retrieve actions occurred within specific time frames, performed by specific users, and so forth.
                </p></li>

              <li><p>
                <span class="font-weight-bold font-italic">Data hub for the CO3 applications.</span>
                The logging service provided by CO3OTM provides full information about the user activities tracked by
                the CO3 applications.
                This means, e.g., that if a user creates a geographical object, the Logger stores information about the
                object itself, and is able to return this type of information to applications, when invoked.
                Thanks to the interlingua format, the data returned by CO3OTM can be understood by any application
                within the platform.
                This function makes the Logger a data hub that can be invoked to retrieve any types of information items
                collected by the CO3 applications and shared in the platform; e.g., geographical objects, initiatives,
                wallets, etc.. <br/>
                Data items are returned as JSON FeatureCollections, i.e., as lists of objects of type Feature, where the
                Feature represents both geographic and non-geographic items. <br/>
                Similar to the APIs for retrieving filtered logs, CO3OTM provides flexible APIs to retrieve shared data
                by applying different types of filters, such as by source application, by concept, by bounding box or by
                distance from a point, by time points and intervals (from, to).
              </p></li>


              <li><p>
                <span class="font-weight-bold font-italic">User achievements management (for gamification).</span>
                CO3OTM manages user profiles storing the achievements obtained by CO3 platform users while interacting
                with the applications and tracks the attributions
                of achievements in order to support the presentation of attribution logs aimed at making the
                achievements transparents to users.
                The management of achievements is based on the following concepts:</p>
                <ul>
                  <li>association of one or more personas to each CO3 user in order to separately store the achievements
                    obtained by the user when acting in the context of specific ACAs, or user groups (or without any
                    reference to any group);
                  </li>
                  <li>event-based management of achievements, depending on which user actions are tracked by CO3OTM:
                    while the logger stores the user actions, the achievement manager of CO3OTM applies the eligible
                    attribution rules in order to add points, badges, etc. to the involved personas. Moreover, the
                    attribution manager manages tasks which make it possible to monitor the state of the various
                    personas in order to apply further attribution rules based on the user’s long-term behavior (e.g.,
                    on her/his behavior within a whole month, or week).
                  </li>
                </ul>
              </li>
            </ol>
            NOTE: CO3OTM is a platform component which collects, aggregates and publishes data about user activities
            that are collected by the front-end applications of the platform;
            this data is sent to the logger by the front-end applications themselves. The Logger doesn’t acquire any
            personal data from users.
            Moreover, it only knows users as pseudonyms represented by their numeric identifiers (UWUM IDs). UWUM IDs
            are used to associate the logged events to the users and to manage the personas associated to users.


          </div>
          <!--Installation Instruction-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h4 id="installation"><strong>Installation Instructions</strong></h4>
            <p>
              This page presents the installation instructions of OnToMap and OTM
              Logger within the WeGovNow platform.
              The page is structured in the following sections:
            </p>
            <ol>
              <li><strong>Project code</strong>: this section reports the link to the GitLab open source
                repository of the application project.
              </li>
              <li><strong>Open Data Management</strong>: OnToMap makes it possible to manage Open Data
                (typically provided by the Municipalities

                of the platform instances). This section provides the following
                information items:
                <ul>
                  <li>link that can be used to download the Open Data provided by

                    the cities of London (Southwark), San Don&agrave; di Piave and Torino,
                    represented as Linked Data (RDF format).
                  </li>
                  <li>link to download the Parliament Triple Store that handles the
                    Linked Data and the instructions to install Parliament and load

                    Open Data in the Triple Store.
                  </li>
                </ul>

              </li>
              <li><strong>User Activity Logging</strong>: the OTM Logger exploits the MongoDB no-SQL
                database to store user activity data. This section provides

                the following information items:

                <ul>
                  <li>link to download MongoDB and instructions to run it.</li>
                  <li>link to download an empty MongoDB database that contains the basic
                    collections of information needed to use MongoDB within the WeGovNow
                    platform.
                  </li>
                </ul>
              </li>
              <li><strong>Run the application</strong>: this section provides instructions to deploy
                OnToMap and OTM Logger.
              </li>
            </ol>
          </div>
          <!--Project Source Code-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h5 id="git_repo"><strong>Project Source Code</strong></h5>
            <p>The project code can be downloaded from <a href="https://gitlab.com/ontomap/ontomap-co3/-/tree/master" target="_blank">GitLab
              Repository</a>.
            </p>
          </div>
          <!--User Activity Logging and Gamification Data-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h4 id="mongo"><strong>User Activity Logging and Gamification Data</strong></h4>
            <p>Logged activity and Gamification data are stored in a NoSQL
              database.
              The current version of OnToMap and OTM Logger uses version <a href="https://fastdl.mongodb.org/win32/mongodb-win32-x86_64-2012plus-4.2.16-signed.msi">4.2 of MongoDB</a> as DBMS.</p>

            <p class="mt-2">
              The database used by OTM Logger is composed of the following 12 collections and are self-installing when the application is first started:
            </p>
            <ol>
              <li><strong>access_tokens_app</strong>: it contains the tokens used by the <a
                  href="https://dev.co3.ontomap.eu/#api-Token_APIs"
                  target="_blank">Token API</a>. Every token is associated to a single
                application and they are used to bypass certificate validation in the cases
                in which certificates cannot be used: for example a GET request made by a client with Javascript
                (AJAX).
              </li>
              <li><strong>access_tokens_user</strong>: it contains a cache of users' access_token.
              </li>
              <li><strong>events</strong>: this is the main collection. Here the events pushed to the OTM Logger
                by using the <a href="https://dev.co3.ontomap.eu/#api-Logger_APIs"
                                target="_blank">Events API </a> are stored.
              </li>
              <li><strong>features</strong>: every time a user activity event is stored, the geographic item that
                is the object of the user action is inserted (or updated) in this collection. This collection is
                used as support to some methods used by Events API, but you can directly interface with this
                list using the <a
                    href="https://dev.co3.ontomap.eu/#api-Instances_APIs-GetConceptInstances" target="_blank">Instances
                  API</a>. The inserted geographic items are represented as features in <a
                    href="http://geojson.org/" target="_blank">GeoJson</a> format, extended with some
                attributes added to indicate the WeGovNow platform instance to which they belong and the name of
                the application that pushed the event to the OTM Logger.
              </li>
              <li><strong>idempotency</strong>: This collection includes keys to prevent an entry request being
                made multiple times. The idempotency-key must be entered in the POST request’s header. Idempotency-keys
                are automatically deleted by the DBMS after 24 hours.
              </li>
              <li><strong>mapping_rules</strong>: This collection contains the mappings between the concepts (with
                attributes) defined in the domain representation adopted by a WeGovNow front-end application and
                the concepts (with attributes) defined in the OnToMap Ontology. These mappings are used at
                runtime in order to translate the information events pushed to the OTM Logger to the interlingua
                provided by the OnToMap Ontology. Every item of this collection refers to a specific WeGovNow
                application and platform instance. See <a
                    href="https://ontomap.eu/#api-Mapping_APIs" target="_blank">Mapping APIs</a>.
              </li>
              <li><strong>subscriptions</strong>: This collection contains the list of subscriptions used by the
                <a
                    href="https://api.ontomap.eu/#api-Subscriptions_APIs" target="_blank">Subscriptions
                  APIs</a>. A subscription must be made by a WeGovNow application indicating the type of event
                to which it wants to subscribe: in this way when an event of that type is pushed to the OTM
                Logger, the Logger will send a notification to the application.
              </li>
              <li><strong>gamification_aca_profiles</strong>:
                This collection contains all Variables and Badges related to ACA.
                These information can be retrieved using <a href="https://dev.co3.ontomap.eu/#api-Gamification_Achievements_Profiles_APIs-Retrieves_ACA_Achievements_Profiles" target="_blank">ACA Achievements Profiles
                APIs</a>
              </li>
              <li><strong>gamification_users_profiles</strong>:
                This collection contains all Variables and Badges related to an user.
                These information can be retrieved using <a href="https://dev.co3.ontomap.eu/#api-Gamification_Achievements_Profiles_APIs-Retrieves_User_Achievements_Profiles" target="_blank">User Achievements Profiles
                  APIs</a>
              </li>
              <li><strong>gamification_badges</strong>:
                This collection contains all Gamification Badges that can be earned by users. These information can be managed using: <a href="https://dev.co3.ontomap.eu/#api-Badge_APIs" target="_blank">Badges APIs</a></li>
              <li><strong>gamification_rules</strong>:
                This collection contains the rules used to assign gamification variables. These information can be manages using: <a href="https://dev.co3.ontomap.eu/#api-Gamification_Rules_APIs" target="_blank">Gamification Rules APIs</a></li>
              <li><strong>gamification_ranking_cache</strong>:
                This collection contains a cache of any king of ranking needed by applications. The default expiration time of each ranking is 5 minutes but it can be modified using configuration properties.
                Ranking can be retrieved using: <a href="https://dev.co3.ontomap.eu/#api-Gamification_Ranking_APIs" target="_blank">Ranking APIS</a></li>
            </ol>
          </div>
          <!--Run the application-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h5 id="runApp"><strong>Run the application</strong></h5>
            <p>
              Please refer to the <a href="https://gitlab.com/ontomap/ontomap-co3/-/blob/master/README.md">Gitlab Project Description</a>.
            </p>
          </div>
          <!--Licenses-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h4 id="licenses"><strong>License</strong></h4>
            <h6 class="mt-4"><strong>Subcomponents shipped With Component</strong></h6>
            <table class="table ">
              <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col">Publisher</th>
                <th scope="col">License</th>
                <th scope="col">URL</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <th scope="row">Play Framework</th>
                <td>Java Server</td>
                <td>Lightbend Inc.</td>
                <td>Apache 2.0</td>
                <td><a href="https://www.playframework.com/">https://www.playframework.com/</a></td>
              </tr>
              <tr>
                <th scope="row">Json Schema Validator</th>
                <td>Java Library</td>
                <td>Java Json Tools</td>
                <td>LGPLv3</td>
                <td><a href="https://github.com/java-json-tools/json-schema-validator">https://github.com/java-json-tools/json-schema-validator</a>
                </td>
              </tr>
              <tr>
                <th scope="row">Http Request</th>
                <td>Java Library</td>
                <td>Kevin Sawicki</td>
                <td>MIT License</td>
                <td><a
                    href="https://github.com/kevinsawicki/http-request">https://github.com/kevinsawicki/http-request</a>
                </td>
              </tr>
              <tr>
                <th scope="row">JSoup</th>
                <td>Java Library</td>
                <td>Jonathan Hedley</td>
                <td>MIT License</td>
                <td><a href="https://github.com/jhy/jsoup">https://github.com/jhy/jsoup</a></td>
              </tr>
              <tr>
                <th scope="row">Jena</th>
                <td>Java Library</td>
                <td>Apache</td>
                <td>Apache 2.0</td>
                <td><a href="https://jena.apache.org/">https://jena.apache.org/</a></td>
              </tr>

              </tbody>
            </table>
            <h6 class="mt-4"><strong>Run-Time Dependencies</strong></h6>
            <table class="table">
              <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col">Publisher</th>
                <th scope="col">License</th>
                <th scope="col">URL</th>
              </tr>
              </thead>
              <tbody>

              <tr>
                <th scope="row">Mongo DB</th>
                <td>Database</td>
                <td>MongoDB Inc</td>
                <td>AGPL v3</td>
                <td><a href="\thttps://www.mongodb.com/">https://www.mongodb.com/</a></td>
              </tr>

              </tbody>
            </table>

            <h6 class="mt-4"><strong>Built-Time Dependencies</strong></h6>
            <table class="table">
              <thead>
              <tr>
                <th scope="col">Name</th>
                <th scope="col">Description</th>
                <th scope="col">Publisher</th>
                <th scope="col">License</th>
                <th scope="col">URL</th>
              </tr>
              </thead>
              <tbody>
              <tr>
                <th scope="row">apiDoc</th>
                <td>Web API Generator</td>
                <td>Peter Rottmann</td>
                <td>MIT License</td>
                <td><a href="http://apidocjs.com">http://apidocjs.com</a></td>
              </tr>
              </tbody>
            </table>
          </div>
          <!--Privacy-->
          <div class="border rounded  bg-light shadow mb-3 p-3">
            <h4 id="privacy"><strong>OnToMap Logging Service and Data Hub (CO3OTM) description and setup of stored data</strong></h4>
            <p>The OnToMap Logging Service and Data Hub (CO3OTM) is a CO3 platform component which collects,
              aggregates and publishes data about user activities that are collected by the front-end applications of the platform.
              Moreover, CO3OTM manages users’ gamification data, based on the activities sent to the logger.
              All data about user activities is sent to the logger by the front-end applications themselves.
              Gamification data are computed by CO3OTM gamification component.
            </p>
            <p>
              CO3OTM manages public, geographic data; it doesn’t verify neither the quality nor the appropriateness of the data sent to it,
              since this task is demanded to the front-end applications originally collecting the data.
              All data published to the Logger by a front-end application is available to all the applications within an instance of the CO3 platform.</p>
            <p>
              CO3OTM also manages users’ gamification data, i.e., achievements that are attributed to users depending on the logged actions.
              Some examples are points, kudos, badges, but the gamification model is
              flexible and can be extended to attribute different types of achievements.</p>
            <p>
              CO3OTM doesn’t have access to any personal data belonging to the users,
              and it uses only their numeric identifier (CO3UUM ID) or wallet address (public key) to make reference to the users.
              The CO3UUM ID is employed to associate the logged events to the users whose actions on the CO3 platform generate
              the corresponding events to be logged (e.g., the editing of a geographic piece of data).
              The CO3UUM ID and wallet address are employed by the gamification engine to associate gamification profiles to individual users and
              possibly apply gamification rules that make reference to wallets.
              All inferences concerning gamification are performed without making explicit reference to users’ personal data and the association
              between numeric identifiers and users’ names is done at the User Interface level by front-end
              applications (e.g., to show the name of the user in her/his profile, or to visualize user rankings)</p>
            <p>


              In the following we list the information collected by the logger, divided by category:</p>

            <h5><strong>Category A (master data)</strong></h5>
            <p>Master data is used to univocally identify a user account.</p>
            <p>The only data pertaining to this category stored by the logger is the numeric user identifier,
              as provided by the CO3 front-end applications requesting the storage of the events,
              and the wallet address (that is the public key associated to a wallet).
            </p>
            <ul>
              <li>Numeric User Identifier (CO3UUM ID)</li>
              <li>Wallet address </li>
            </ul>

            <h5><strong>Category B (identification data)</strong></h5>
            <p>Identification data is used to associate a user account with an accredited person.</p>
            <p>The CO3OTM does not store any data pertaining to this category.</p>

            <h5><strong>Category C (profile data)</strong></h5>
            <p>Profile data is voluntary information provided by the participant about
              themselves and may be changed or deleted at any time by the participant.
            </p>
            <p>The OnToMap Logging Service and Data Hub (CO3OTM)
              does not store any data pertaining to this category.</p>

            <h5><strong>Category D (content data)</strong></h5>
            <p>Content data is associated to each event representing a user action,
              and provides a description of the user action itself.
            </p>
            <p>CO3OTM does not acquire any piece of content data itself:
              it only stores the data items belonging to the events pushed to it by the front-end applications.
              Moreover, CO3OTM generates content data (gamification events and gamification profile data) as
              a consequence of the application of gamification rules.
            </p>
            <p>The mandatory content data items required for all the events to be logged are:</p>
            <ul>
              <li>Type of user action</li>
              <li>Timestamp of the action</li>
              <li>CO3UUM ID of the user</li>
            </ul>

            <h5><strong>Category E (usage data)</strong></h5>
            <p>Usage data refers to the actions performed by the users in the CO3 front-end applications.</p>
            <p>CO3OTM does not acquire any piece of content data itself:
              it only stores the data items belonging to the events pushed to it by the front-end applications.
            </p>

            <h5><strong>Category F (login and settings data)</strong></h5>
            <p>Login and settings data is only intended for use by a particular user and is required for operation.</p>
            <p>The OnToMap Logging Service and Data Hub (CO3OTM) does not store any data pertaining to this category.       </p>

            <h5><strong>Category G (web server log data)</strong></h5>
            <p>Web server log data is generated whenever the logging component of CO3OTM is used,
              either to store or to retrieve events.</p>
            <ul>
              <li>Source of request (IP address)</li>
              <li>Timestamp of request</li>
              <li>Requested URL</li>
              <li>HTTP Referer</li>
              <li>HTTP User Agent</li>
              <li>Size of response</li>
              <li>Status code of response</li>
            </ul>
            <p>Users will have access to data pertaining to categories A, D, and E, through the front-end
              applications. Data pertaining to category G is not available to any user or WeGovNow front-end
              application.</p>


            <h5><strong>Database Dumps</strong></h5>
            <p>The database dumps contain data from categories A, D and E.</p>

            <h5><strong>Cookies</strong></h5>
            <p>Since CO3OTM does not offer a user interface, it does not store cookies.</p>

            <h5><strong>Data deletion for the OnToMap Logging Service and Data Hub (CO3OTM)</strong></h5>
            <p>All data stored in the OnToMap Logging Service and Data Hub (CO3OTM) can be completely
              and permanently deleted by the Data Controller (University of Torino).
              Doing so, however, could lead to a loss of traceability of user actions</p>
            <p>Upon request, it is possible to delete the following types of data:</p>
            <ul>
              <li>Individual data items (logged events, applied gamification rules, gamification profiles)</li>
              <li>Data items containing the ID of a specific user (events concerning that user)</li>
              <li>Data originating from a specific application (events pushed to the logger by that application) </li>
            </ul>
          </div>
        </div>

      </div>
      <div class="row justify-content-md-center mt-4">
        <div class="col-md-4 rounded text-center p-4">
          This project has received funding from the European Union\s Horizon 2020 research and innovation
          programme under grant agreement No <a href="https://cordis.europa.eu/project/id/822615"
                                                target="_blank">822615</a><br>
          <img src="images/eu.png" alt="EU emblem">
        </div>
        <div class="offset-md-1 col-md-4 text-center p-4">
          Computer Science Department<br/> University of Torino <br/> <a
            href="http://www.dipinformatica.unito.it/" target="_blank"><img class="w-50"
                                                                            src="images/logo_dipartimento.jpg"/></a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CO3",
  data () {
    return {
      config: {
        element: "body",
        offset: 10,
        method: "auto",
        throttle: 100
      }
    }
  }
}
</script>

<style scoped>

</style>
